import { useId } from 'react';

import { css, keyframes } from '@compiled/react';

import Avatar from '@atlaskit/avatar';
import BoardIcon from '@atlaskit/icon/core/board';
import TableIcon from '@atlaskit/icon/core/table';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { getInitials } from '@/components/site-selector-modal/utils/initials';
import type { CodingAgentId } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/coding-agent-choice/types';
import inviteeBlueAvatar from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/wizard/variant/jfind-onboard-illustration-refresh/assets/invitee-blue-avatar.png';
import inviteeYellowAvatar from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/wizard/variant/jfind-onboard-illustration-refresh/assets/invitee-yellow-avatar.png';
import modernAvatar from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/wizard/variant/jfind-onboard-illustration-refresh/assets/modern-avatar.png';
import { JfindAgentAvatar } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/wizard/variant/jfind-onboard-illustration-refresh/jfind-agent-avatar';
import { JfindIllustrationInitialsAvatar } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/wizard/variant/jfind-onboard-illustration-refresh/jfind-illustration-initials-avatar';
import type { ViewType } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/wizard/variant/jfind-onboard-illustration-refresh/jfind-illustration-types';
import { useLoggedInUser } from '@/utils/logged-in-user-provider';

type Props = {
	projectName?: string;
	viewType?: ViewType;
	isInviteStep?: boolean;
	selectedAgents?: CodingAgentId[];
};

/**
 * Project header for the JFind onboard illustration.
 * Shows project avatar, name, board/list tab strip, and avatar group.
 */
export const JfindIllustrationProjectHeader = ({
	projectName = 'My Software Team',
	viewType = 'board',
	isInviteStep = false,
	selectedAgents,
}: Props) => {
	const gradientId = useId();
	const { name } = useLoggedInUser();

	const userInitials = name ? getInitials(name) : 'MC';
	return (
		<Box xcss={projectHeaderStyles}>
			<Flex xcss={projectTitleRowStyles} alignItems="center" gap="space.150">
				{/* Project avatar — exact Figma SVG */}
				<svg
					width="40"
					height="40"
					viewBox="0 0 41 41"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					aria-hidden
					style={{ flexShrink: 0 }}
				>
					<g clipPath={`url(#${gradientId}a)`}>
						<g clipPath={`url(#${gradientId}b)`}>
							<path d="M40.32 0H0V40.32H40.32V0Z" fill="#BF63F3" />
							<path d="M5.64502 7.57031V32.8711H34.6653V7.57031H5.64502Z" fill="white" />
							<path
								d="M15.4228 33.6793C15.4329 33.4474 14.5156 32.7922 14.3039 32.9132C13.9914 33.0745 13.7394 33.2962 13.5177 33.649C13.3161 34.0825 13.4773 34.2942 13.306 34.5663C13.054 35.03 12.3988 35.7255 12.2476 35.8969C12.2274 35.9271 12.2073 35.9473 12.2073 35.9876C12.1569 36.2194 11.9452 37.2274 11.6428 38.0338C11.4714 38.6286 11.3908 38.9914 11.3505 39.183C11.2597 39.6366 11.3001 40.0095 11.4513 40.3724C11.5319 40.574 12.4996 40.7353 12.6508 40.5841C12.9129 40.3119 13.0943 40.0196 13.1951 39.6063C13.2354 39.445 13.5681 39.1729 13.7495 38.7294C13.7797 38.6588 13.7797 38.5782 13.7596 38.4874C13.5882 37.7314 14.0116 36.9452 14.0116 36.9452C14.0116 36.9452 14.1426 36.6428 14.2938 36.2799C14.4551 35.917 14.6668 35.5038 14.7978 35.2618C14.9893 34.899 15.0901 34.7276 15.1506 34.6167C15.3321 34.2942 15.4127 34.0119 15.4329 33.6793H15.4228Z"
								fill="#101214"
							/>
							<path
								d="M27.5995 40.524C27.7507 40.6954 28.8595 40.5139 28.9401 40.2821C29.051 39.9595 29.0812 39.6269 29.0006 39.2338C28.8393 38.7903 28.5873 38.7399 28.5369 38.4375C28.4059 37.9335 28.426 36.996 28.4159 36.7541C28.4159 36.7239 28.4159 36.6835 28.3958 36.6634C28.2748 36.4517 27.7607 35.5848 27.4382 34.7683C27.1559 34.224 26.9644 33.8914 26.8636 33.72C26.6217 33.3168 26.3294 33.085 25.9564 32.9237C25.7548 32.833 24.9585 33.4075 24.9585 33.6091C24.9585 33.9922 25.0391 34.3047 25.2407 34.6776C25.3214 34.8187 25.2609 35.2421 25.4323 35.6856C25.4625 35.7562 25.5129 35.8167 25.5835 35.8671C26.2084 36.3005 26.4302 37.1674 26.4302 37.1674C26.4302 37.1674 26.5411 37.4698 26.6721 37.8427C26.7931 38.2157 26.9241 38.6592 26.9947 38.9314C27.0955 39.3346 27.1459 39.5261 27.1761 39.6471C27.2668 40.0099 27.4079 40.272 27.6196 40.524H27.5995Z"
								fill="#101214"
							/>
							<path
								d="M7.66113 9.58594V18.6479L9.16305 16.5311C10.9472 14.0111 14.697 14.0111 16.4811 16.5311L19.3136 20.5328C20.8962 22.7605 24.0815 23.073 26.0571 21.1981C27.6397 19.6962 30.0287 19.5853 31.7423 20.8251V9.59602H7.66113V9.58594Z"
								fill="#1868DB"
							/>
							<path
								d="M24.8073 16.6825C24.8375 16.5112 24.8577 16.3196 24.8577 16.1281C24.8577 15.9366 24.8375 15.735 24.8073 15.5636C24.6158 14.5052 23.89 13.8601 22.8619 13.6888C22.6905 13.6585 22.499 13.6484 22.3075 13.6484C22.1159 13.6484 21.9143 13.6686 21.7329 13.6988C20.7047 13.8702 19.979 14.5153 19.7875 15.5636C19.7572 15.7451 19.7371 15.9265 19.7371 16.1281C19.7371 16.3297 19.7572 16.5112 19.7875 16.6825C19.979 17.7308 20.7047 18.376 21.7329 18.5473C21.9143 18.5776 22.1059 18.5977 22.3075 18.5977C22.5091 18.5977 22.6804 18.5876 22.8619 18.5574C23.89 18.386 24.6259 17.7409 24.8073 16.6825Z"
								fill="white"
							/>
							<path
								d="M31.7523 30.8566V20.8169C30.0286 19.5771 27.6396 19.698 26.0671 21.1899C24.0915 23.0647 20.8961 22.7523 19.3236 20.5246L16.4911 16.5228C14.707 14.0028 10.9572 14.0028 9.17306 16.5228L7.67114 18.6396V30.8465H31.7623L31.7523 30.8566Z"
								fill="#D8A0F7"
							/>
							<path
								d="M20.7551 3.89226C20.6342 3.80154 19.5153 3.80154 19.3943 3.89226C19.223 4.0233 19.1423 4.1745 19.1323 4.35594C19.1827 4.56762 19.4145 4.61802 19.3943 4.75914C19.3943 5.00106 19.0819 5.39418 19.0012 5.49498C18.9911 5.50506 18.9911 5.52522 19.0012 5.5353C19.0415 5.6361 19.2129 6.0897 19.1323 6.47274C19.1323 6.7449 19.1323 6.91626 19.1323 7.00698C19.1323 7.21866 19.2532 7.36986 19.4649 7.52106C19.5859 7.6017 20.5636 7.6017 20.6745 7.52106C20.8862 7.36986 21.0071 7.20858 21.0071 7.00698C21.0071 6.92634 21.2793 6.75498 21.3196 6.52314C21.3196 6.48282 21.3095 6.4425 21.2692 6.41226C20.9063 6.07962 21.0071 5.64618 21.0071 5.64618C21.0071 5.64618 21.0071 5.00106 21.0071 4.7289C21.0071 4.5273 21.0071 4.4265 21.0071 4.36602C21.0071 4.18458 20.9063 4.04346 20.7451 3.90234L20.7551 3.89226Z"
								fill="#101214"
							/>
							<path
								d="M6.68318 32.2865C5.30222 32.4074 5.30222 33.5263 6.68318 33.6473C8.6387 33.8186 10.7051 33.8993 13.3461 33.9094C16.3499 33.859 17.0958 33.6271 19.1219 33.6473C22.5592 33.6473 28.3451 33.9598 29.7462 34.0404C29.9478 34.0505 30.1595 34.0505 30.3712 34.0404C30.603 34.0404 30.9659 34.0303 31.6211 34.0102C32.5182 34.0102 33.8185 33.99 35.764 33.9698C35.7539 33.1836 35.7237 31.4095 35.6934 29.2121C35.6733 27.5287 35.6733 25.5127 35.6934 23.6076C35.6934 19.6663 35.6934 17.2169 35.6934 15.9468C35.6934 12.9329 35.5725 10.685 35.3608 8.47751C35.2398 7.26791 34.2621 7.26791 34.1512 8.47751C33.9395 10.685 33.8185 12.943 33.8185 15.9468C33.8185 17.1161 33.5464 19.5554 33.5061 23.0028C33.5061 23.5673 33.5161 24.1217 33.5565 24.6761C33.6673 26.0772 33.7278 27.5892 33.7681 29.0206C33.7883 30.1394 33.8085 31.1878 33.8286 32.0143C32.5989 32.0143 31.8328 32.0042 31.8328 32.0042C31.8328 32.0042 22.5289 32.0042 18.6078 32.0042C15.6544 32.0042 14.2331 32.0042 13.3561 32.0042C10.7051 32.0042 8.64878 32.105 6.69326 32.2663L6.68318 32.2865Z"
								fill="#101214"
							/>
						</g>
					</g>
					<defs>
						<clipPath id={`${gradientId}a`}>
							<rect width="40.32" height="40.32" rx="12" fill="white" />
						</clipPath>
						<clipPath id={`${gradientId}b`}>
							<rect width="40.32" height="40.32" fill="white" />
						</clipPath>
					</defs>
				</svg>
				<div css={projectNameCss}>{projectName}</div>
			</Flex>

			{/* Tabs */}
			<Flex xcss={tabsRowStyles} alignItems="center">
				{viewType === 'board' ? (
					<>
						<div css={tabActiveStyles}>
							<BoardIcon label="" />
							<span>Board</span>
						</div>
						<div css={tabInactiveStyles}>
							<TableIcon label="" />
							<span>List</span>
						</div>
					</>
				) : (
					<>
						<div css={tabActiveStyles}>
							<TableIcon label="" />
							<span>List</span>
						</div>
						<div css={tabInactiveStyles}>
							<BoardIcon label="" />
							<span>Board</span>
						</div>
					</>
				)}
			</Flex>

			{/* Avatar group */}
			<Flex xcss={avatarGroupRowStyles} alignItems="center">
				<JfindIllustrationInitialsAvatar
					initials={userInitials}
					backgroundColor={token('color.icon.accent.lime')}
				/>
				<div css={avatarOverlapStyles}>
					<Avatar size="small" src={modernAvatar} />
				</div>
				{isInviteStep && (
					<>
						<div
							css={[avatarOverlapStyles, avatarPopInBaseCss]}
							style={{ animationDelay: '500ms' }}
						>
							<Avatar size="small" src={inviteeYellowAvatar} />
						</div>
						<div
							css={[avatarOverlapStyles, avatarPopInBaseCss]}
							style={{ animationDelay: '600ms' }}
						>
							<Avatar size="small" src={inviteeBlueAvatar} />
						</div>
					</>
				)}
				{selectedAgents &&
					selectedAgents.length > 0 &&
					selectedAgents.map((agentId, index) => (
						<div
							key={agentId}
							css={[avatarOverlapStyles, avatarPopInBaseCss]}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
							style={{ animationDelay: `${(index + 1) * 100}ms` }}
						>
							<JfindAgentAvatar agentId={agentId} size={24} />
						</div>
					))}
			</Flex>
		</Box>
	);
};

const projectHeaderStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	gap: 'space.150',
	paddingBlockStart: 'space.400',
	paddingBlockEnd: 'space.100',
	flexShrink: 0,
});

const projectTitleRowStyles = xcss({
	paddingInlineStart: 'space.400',
});

const projectNameCss = css({
	font: token('font.heading.xlarge'),
	fontFamily:
		"Charlie Display, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif",
	color: token('color.text'),
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

const tabsRowStyles = xcss({
	gap: 'space.050',
	minHeight: '32px',
	borderBlockEnd: '1px solid',
	borderColor: 'color.border',
	paddingInlineStart: 'space.400',
	flexShrink: 0,
});

const tabActiveStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
	paddingInline: token('space.075'),
	paddingBlock: token('space.050'),
	color: token('color.text.selected'),
	fontSize: '14px',
	fontWeight: token('font.weight.medium'),
	borderBlockEnd: `3px solid ${token('color.border.selected')}`,
	marginBlockEnd: '-1px',
	whiteSpace: 'nowrap',
});

const tabInactiveStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
	paddingInline: token('space.075'),
	paddingBlock: token('space.050'),
	color: token('color.text.subtle'),
	fontSize: '14px',
	fontWeight: token('font.weight.medium'),
	whiteSpace: 'nowrap',
});

const avatarPopInKeyframes = keyframes({
	from: { opacity: 0, transform: 'scale(0.5)' },
	to: { opacity: 1, transform: 'scale(1)' },
});

const avatarPopInBaseCss = css({
	animationName: avatarPopInKeyframes,
	animationDuration: '200ms',
	animationTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
	animationFillMode: 'both',
});

const avatarOverlapStyles = css({
	marginInlineStart: '-4px',
	lineHeight: 0,
});

const avatarGroupRowStyles = xcss({
	paddingInlineStart: 'space.400',
	paddingBlockEnd: 'space.100',
	flexShrink: 0,
});
